import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, ArrowLeft, Printer, RotateCw } from "lucide-react";

import { useTranslation } from "@/i18n/use-t";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button, buttonVariants } from "@/components/ui/button";
import { SerialInput } from "@/components/SerialInput";
import { ProductCard } from "@/components/ProductCard";
import {
  lookupByNamespaceAndSerial,
  lookupBySerial,
} from "@/api/public-units";
import {
  NotFoundError,
  RateLimitedError,
} from "@/api/client";

/**
 * Route for direct-link / form-submit lookups. Search params:
 *   ?ns=<scheme-code>  — disambiguate when a serial alone could match many
 *                        schemes (rare; only printed on QR codes in mixed
 *                        product lines).
 */
export const Route = createFileRoute("/s/$serial")({
  validateSearch: (search: Record<string, unknown>) => ({
    ns: typeof search.ns === "string" ? search.ns : undefined,
  }),
  component: SerialResultPage,
});

function SerialResultPage() {
  const { serial } = Route.useParams();
  const { ns } = Route.useSearch();
  const { t } = useTranslation("lookup");

  const query = useQuery({
    queryKey: ["public-unit", ns ?? "*", serial],
    queryFn: ({ signal }) =>
      ns ? lookupByNamespaceAndSerial(ns, serial, signal) : lookupBySerial(serial, signal),
  });

  if (query.isPending) {
    return <LoadingSkeleton label={t("result.loading")} />;
  }

  if (query.error) {
    if (query.error instanceof NotFoundError) {
      return <NotFoundCard onRetry={() => query.refetch()} />;
    }
    if (query.error instanceof RateLimitedError) {
      return (
        <RateLimitedCard
          retryAfterSeconds={query.error.retryAfterSeconds}
          onRetry={() => query.refetch()}
        />
      );
    }
    return <GenericErrorCard onRetry={() => query.refetch()} />;
  }

  return <ResultView unit={query.data} />;
}

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

function LoadingSkeleton({ label }: { label: string }) {
  return (
    <Card aria-busy aria-live="polite">
      <CardHeader className="space-y-3">
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="h-4 w-1/2" />
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Skeleton className="h-14" />
          <Skeleton className="h-14" />
          <Skeleton className="h-14" />
          <Skeleton className="h-14" />
        </div>
        <p className="sr-only">{label}</p>
      </CardContent>
    </Card>
  );
}

function ResultView({ unit }: { unit: import("@/api/types").PublicUnitDto }) {
  const { t } = useTranslation("product");
  return (
    <div className="space-y-6">
      <div data-print-hide className="flex flex-wrap items-center justify-between gap-2">
        <Link
          to="/"
          className={buttonVariants({ variant: "ghost", size: "sm" })}
        >
          <ArrowLeft />
          {t("lookupAnother")}
        </Link>
        <Button variant="outline" size="sm" onClick={() => window.print()}>
          <Printer />
          {t("print")}
        </Button>
      </div>
      <ProductCard unit={unit} />
    </div>
  );
}

function NotFoundCard({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation("errors");
  return (
    <Card role="alert">
      <CardHeader className="space-y-3">
        <div className="flex items-center gap-2 text-destructive">
          <AlertCircle aria-hidden className="h-5 w-5" />
          <CardTitle className="text-xl">{t("notFound.title")}</CardTitle>
        </div>
        <p className="text-base text-muted-foreground">
          {t("notFound.message")}
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        <SerialInput autoFocus />
        <Button variant="ghost" onClick={onRetry} className="text-sm">
          <RotateCw />
          {t("notFound.retry")}
        </Button>
      </CardContent>
    </Card>
  );
}

function RateLimitedCard({
  retryAfterSeconds,
  onRetry,
}: {
  retryAfterSeconds: number;
  onRetry: () => void;
}) {
  const { t } = useTranslation("errors");
  const [remaining, setRemaining] = useState(retryAfterSeconds);

  // Single-shot countdown — once it hits 0 the retry button activates.
  // Resets when `retryAfterSeconds` changes (another 429 mid-countdown).
  useEffect(() => {
    setRemaining(retryAfterSeconds);
    if (retryAfterSeconds <= 0) return;
    const id = window.setInterval(() => {
      setRemaining((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [retryAfterSeconds]);

  const ready = remaining <= 0;

  return (
    <Card role="alert">
      <CardHeader className="space-y-2">
        <CardTitle className="text-xl">{t("rateLimited.title")}</CardTitle>
        <p className="text-base text-muted-foreground">
          {t("rateLimited.message")}
        </p>
      </CardHeader>
      <CardContent>
        <Button onClick={onRetry} disabled={!ready}>
          <RotateCw />
          {ready
            ? t("notFound.retry")
            : t("rateLimited.countdown", { seconds: remaining })}
        </Button>
      </CardContent>
    </Card>
  );
}

function GenericErrorCard({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation("errors");
  return (
    <Card role="alert">
      <CardHeader className="space-y-2">
        <div className="flex items-center gap-2 text-destructive">
          <AlertCircle aria-hidden className="h-5 w-5" />
          <CardTitle className="text-xl">{t("generic.title")}</CardTitle>
        </div>
        <p className="text-base text-muted-foreground">{t("generic.message")}</p>
      </CardHeader>
      <CardContent>
        <Button onClick={onRetry}>
          <RotateCw />
          {t("notFound.retry")}
        </Button>
      </CardContent>
    </Card>
  );
}
